// src/utils/lazyWithRetry.tsx
// Lazy-загрузка с автоперезагрузкой при ChunkLoadError (только для chunk-ошибок)
import { lazy, ComponentType } from 'react';

const RELOAD_KEY = 'chunk_reload_count';
const RELOAD_TS_KEY = 'chunk_reload_last';
const MAX_RELOADS = 2;
const RELOAD_TIMEOUT = 10000; // 10 сек между перезагрузками

function forceReload() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_t', Date.now().toString());
    console.warn('🔄 [lazyWithRetry] Force reload с обходом кеша');
    window.location.replace(url.toString());
  } catch (e) {
    window.location.reload();
  }
}

// Сброс счётчика — вызывается при успешной загрузке модуля
export function resetChunkRetry() {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_TS_KEY);
  } catch (e) {}
}

// ВАЖНО: только chunk-специфичные ошибки, не любые
function isChunkLoadError(error: any): boolean {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || '');

  // webpack chunk load failed
  if (name === 'ChunkLoadError') return true;
  if (/Loading chunk \d+ failed/i.test(message)) return true;
  if (/Loading CSS chunk \d+ failed/i.test(message)) return true;
  if (/Failed to fetch dynamically imported module/i.test(message)) return true;

  // НЕ триггерим на AxiosError / HTTP 500 / прочие
  if (name === 'AxiosError') return false;
  if (/status code 5\d\d/i.test(message)) return false;
  if (/Request failed with status code/i.test(message)) return false;

  return false;
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory()
      .then((mod) => {
        // ★ Успешная загрузка — сбрасываем счётчик
        resetChunkRetry();
        return mod;
      })
      .catch((error: any) => {
        // Реагируем ТОЛЬКО на chunk-ошибки
        if (!isChunkLoadError(error)) {
          console.error('❌ [lazyWithRetry] Не chunk-ошибка, пробрасываем:', error?.name, error?.message);
          throw error;
        }

        const now = Date.now();
        const lastTs = Number(sessionStorage.getItem(RELOAD_TS_KEY) || '0');
        const count = Number(sessionStorage.getItem(RELOAD_KEY) || '0');

        console.warn(`🔄 [lazyWithRetry] ChunkLoadError #${count + 1} (было ${now - lastTs}мс назад)`);

        // Антифлуд — если перезагружались недавно, ждём
        if (now - lastTs < RELOAD_TIMEOUT && count >= MAX_RELOADS) {
          console.error('❌ [lazyWithRetry] Слишком много перезагрузок — показываем заглушку');
          return {
            default: (() => (
              <div style={{ padding: 60, textAlign: 'center', color: '#64748b', fontFamily: 'system-ui' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔄</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>
                  Не удалось загрузить модуль
                </div>
                <div style={{ fontSize: 13, marginBottom: 20 }}>
                  Кеш браузера содержит устаревшие данные
                </div>
                <button
                  onClick={() => {
                    resetChunkRetry();
                    forceReload();
                  }}
                  style={{
                    padding: '12px 24px', background: 'linear-gradient(135deg, #2575fc, #6a11cb)',
                    color: '#fff', border: 'none', borderRadius: 10, fontSize: 14,
                    fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Перезагрузить с обходом кеша
                </button>
              </div>
            )) as unknown as T,
          };
        }

        // Обновляем счётчик
        sessionStorage.setItem(RELOAD_KEY, String(count + 1));
        sessionStorage.setItem(RELOAD_TS_KEY, String(now));

        // Задержка и перезагрузка
        setTimeout(() => forceReload(), 50);

        return {
          default: (() => (
            <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: 14 }}>Обновление приложения…</div>
            </div>
          )) as unknown as T,
        };
      })
  );
}

export default lazyWithRetry;
